import type { ComponentTemplate } from './types';
import { buildDefaultLibrary } from './seedTemplates';

const STORAGE_KEY = 'skema:user-library';
const LEGACY_STORAGE_KEY = 'robot-diagram:user-library';
// Set once the starter library has been offered. After that defaults are
// never re-added, so deleting a default part stays deleted instead of
// resurrecting on every load. Bump the suffix to re-offer new defaults.
const SEED_FLAG = 'skema:lib-seeded-v1';

function migrateLegacy(): void {
  try {
    if (localStorage.getItem(STORAGE_KEY)) return;
    const legacy = localStorage.getItem(LEGACY_STORAGE_KEY);
    if (legacy) {
      localStorage.setItem(STORAGE_KEY, legacy);
      localStorage.removeItem(LEGACY_STORAGE_KEY);
    }
  } catch {
    // ignore
  }
}

/** Seed the starter library (once). Existing users keep everything they have —
 *  defaults are only added when neither the key nor the label already exists
 *  (older installs added these same parts via the "Add template…" dropdown,
 *  which used random keys, so the label check prevents duplicates). */
function ensureSeeded(current: ComponentTemplate[]): ComponentTemplate[] {
  try {
    if (localStorage.getItem(SEED_FLAG)) return current;
    const haveKeys = new Set(current.map((t) => t.key));
    const haveLabels = new Set(current.map((t) => t.label.trim().toLowerCase()));
    const missing = buildDefaultLibrary().filter(
      (d) => !haveKeys.has(d.key) && !haveLabels.has(d.label.trim().toLowerCase()),
    );
    const next = missing.length > 0 ? [...current, ...missing] : current;
    if (missing.length > 0) save(next);
    localStorage.setItem(SEED_FLAG, '1');
    return next;
  } catch {
    return current;
  }
}

function load(): ComponentTemplate[] {
  migrateLegacy();
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return ensureSeeded(Array.isArray(parsed) ? (parsed as ComponentTemplate[]) : []);
  } catch {
    return [];
  }
}

function save(templates: ComponentTemplate[]): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(templates));
}

export function listTemplates(): ComponentTemplate[] {
  return load();
}

export function getTemplate(key: string): ComponentTemplate | undefined {
  return load().find((t) => t.key === key);
}

export function upsertTemplate(template: ComponentTemplate): void {
  const all = load();
  const idx = all.findIndex((t) => t.key === template.key);
  if (idx >= 0) all[idx] = template;
  else all.push(template);
  save(all);
}

export function deleteTemplate(key: string): void {
  save(load().filter((t) => t.key !== key));
}

export function makeBlankTemplate(): ComponentTemplate {
  return {
    key: `tpl-${Date.now().toString(36)}`,
    label: 'New Component',
    category: 'Custom',
    partNumber: '',
    manufacturer: '',
    defaultProperties: {},
    pins: [],
    color: '#334155',
  };
}

export interface LibraryFile {
  version: 1;
  kind: 'skema-library' | 'robot-diagram-library';
  exportedAt: string;
  templates: ComponentTemplate[];
}

export function exportLibraryJson(): string {
  const file: LibraryFile = {
    version: 1,
    kind: 'skema-library',
    exportedAt: new Date().toISOString(),
    templates: load(),
  };
  return JSON.stringify(file, null, 2);
}

/** Validate + parse a library file WITHOUT persisting. Throws on bad input. */
export function parseLibraryTemplates(text: string): ComponentTemplate[] {
  const parsed = JSON.parse(text);
  if (
    typeof parsed !== 'object' ||
    (parsed.kind !== 'skema-library' && parsed.kind !== 'robot-diagram-library') ||
    parsed.version !== 1 ||
    !Array.isArray(parsed.templates)
  ) {
    throw new Error('Not a valid Skematiq library file.');
  }
  return parsed.templates as ComponentTemplate[];
}

/** Overwrite the whole stored library (used by the merge/import preview). */
export function setAllTemplates(templates: ComponentTemplate[]): void {
  save(templates);
}

export function importLibraryJson(
  text: string,
  mode: 'merge' | 'replace',
): { added: number; updated: number; total: number } {
  const incoming = parseLibraryTemplates(text);
  if (mode === 'replace') {
    save(incoming);
    return { added: incoming.length, updated: 0, total: incoming.length };
  }
  const current = load();
  const byKey = new Map(current.map((t) => [t.key, t]));
  let added = 0;
  let updated = 0;
  for (const t of incoming) {
    if (byKey.has(t.key)) updated += 1;
    else added += 1;
    byKey.set(t.key, t);
  }
  const merged = Array.from(byKey.values());
  save(merged);
  return { added, updated, total: merged.length };
}
